import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { usePropertyDetail } from '../hooks/usePropertyDetail';
import { useUpdateProperty } from '../hooks/useUpdateProperty';
import { useImageUpload } from '../hooks/useImageUpload';
import { useDeleteImage } from '../hooks/useDeleteImage';
import { useDeleteFeature } from '../hooks/useDeleteFeature';
import LoadingSpinner from '../components/LoadingSpinner';
import { toast } from 'react-toastify';
import { ArrowLeft, Plus, X, Upload, Trash2, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';

const EditProperty = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { data, isLoading } = usePropertyDetail(id);
  const property = data?.property;
  const { mutate: updateProperty, isPending: isUpdating } = useUpdateProperty();
  const { mutate: deleteImage } = useDeleteImage();
  const { mutate: deleteFeature } = useDeleteFeature();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    address: '',
    type: '',
    pricePerNight: '',
    size: '',
    bedrooms: '',
    bathrooms: '',
    status: '',
    newImages: [],
    features: [],
  });

  const [isLoadingDeletions, setIsLoadingDeletions] = useState(false);
  const commonFeatures = [
    'Furnished',
    'WiFi',
    'Parking',
    'Garden',
    'Balcony',
    'Air Conditioning',
    'Heating',
    'Dishwasher',
  ];

  const { dragActive, handleImageInput, handleDrag, handleDrop, removeImage, moveImage, draggedIndex, handleImageDragStart, handleImageDragOver, handleImageDrop } = useImageUpload((updateFn) => {
    setFormData((prev) => ({
      ...prev,
      newImages: updateFn(prev.newImages),
    }));
  });

  useEffect(() => {
    if (property) {
      setFormData({
        title: property.title || '',
        description: property.description || '',
        address: property.address || '',
        type: property.type || '',
        pricePerNight: property.price || 0,
        size: property.size || 0,
        bedrooms: property.bedrooms || 1,
        bathrooms: property.bathrooms || 1,
        status: property.status || 'active',
        newImages: [],
        features: property.features?.map(f => ({ 
          id: f.id, 
          name: f.feature || f 
        })) || [],
      });
    }
  }, [property]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value === '' ? '' : isNaN(value) ? value : Number(value),
    }));
  };

  const handleArrayInput = (e, field) => {
    const value = e.target.value.trim();
    if (e.key === 'Enter' && value) {
      e.preventDefault();
      setFormData((prev) => ({
        ...prev,
        [field]: [...prev[field], { id: null, name: value }],
      }));
      e.target.value = '';
    }
  };

  const removeArrayItem = (field, index) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const toggleFeature = (feature) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.some((f) => f.name === feature)
        ? prev.features.filter((f) => f.name !== feature)
        : [...prev.features, { id: null, name: feature }],
    }));
  };

  const handleDeleteImage = (imageId) => {
    const existingImages = property?.images || [];
    
    // Check if this is the last image
    if (existingImages.length === 1) {
      toast.error('Cannot delete the last image. Every property must have at least 1 image.');
      return;
    }
    
    setIsLoadingDeletions(true);
    deleteImage({ propertyId: id, imageId }, {
      onSuccess: () => {
        setIsLoadingDeletions(false);
        toast.success('Image deleted successfully');
        queryClient.invalidateQueries({ queryKey: ['propertyDetail', id] });
      },
      onError: (error) => {
        setIsLoadingDeletions(false);
        toast.error(error.message || 'Failed to delete image');
      },
    });
  };

  const handleDeleteFeature = (featureId) => {
    setIsLoadingDeletions(true);
    deleteFeature({ propertyId: id, featureId }, {
      onSuccess: () => {
        setIsLoadingDeletions(false);
        setFormData((prev) => ({
          ...prev,
          features: prev.features.filter((f) => f.id !== featureId),
        }));
        toast.success('Feature deleted successfully');
        queryClient.refetchQueries({ queryKey: ['propertyDetail', id] });
      },
      onError: (error) => {
        setIsLoadingDeletions(false);
        toast.error(error.message || 'Failed to delete feature');
      },
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.title || !formData.address || !formData.type || !formData.pricePerNight || !formData.size || !formData.bedrooms || !formData.bathrooms) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.features.length === 0) {
      toast.error('Please select at least 1 feature');
      return;
    }

    // Check if property has at least one image (existing or new)
    const existingImages = property?.images || [];
    const hasImages = existingImages.length > 0 || formData.newImages.length > 0;
    
    if (!hasImages) {
      toast.error('Please add at least 1 image');
      return;
    }

    const loadingToastId = toast.loading('Updating property...');
    updateProperty({ id, formData, originalData: property }, {
      onSuccess: () => {
        toast.dismiss(loadingToastId);
        toast.success('Property updated successfully!');
        queryClient.refetchQueries({ queryKey: ['propertyDetail', id] });
        queryClient.invalidateQueries({ queryKey: ['properties'] });
        navigate('/properties');
      },
      onError: (error) => {
        toast.dismiss(loadingToastId);
        toast.error(error.message || 'Failed to update property');
      },
    });
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading property..." />;
  }

  if (!property?.id) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <p className="text-lg text-prose-black/60 mb-4">Property not found</p>
        <button
          onClick={() => navigate('/properties')}
          className="px-4 py-2 bg-prose-brand text-white rounded-lg hover:bg-prose-brand/90"
        >
          Back to Properties
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-start space-x-3 sm:space-x-4">
        <button
          onClick={() => navigate('/properties')}
          className="p-2 hover:bg-gray-100 rounded-lg transition duration-200 flex-shrink-0"
        >
          <ArrowLeft size={20} className="text-prose-brand" />
        </button>
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-prose-brand break-words">Edit Property</h1>
          <p className="text-xs sm:text-sm text-prose-black/60 mt-1">Update property details</p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg border border-gray-300 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-bold text-prose-brand mb-4">Basic Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-prose-black mb-1">
                Property Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="e.g., Luxury Apartment in City Centre"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-prose-black mb-1">
                Address *
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Full address"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand text-sm"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-prose-black mb-1">
                Property Type *
              </label>
              <input
                type="text"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                placeholder="e.g., Apartment, House"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand text-sm"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-prose-black mb-1">
                Price Per Night (£) *
              </label>
              <input
                type="number"
                name="pricePerNight"
                value={formData.pricePerNight}
                onChange={handleInputChange}
                placeholder="e.g., 150"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand text-sm"
              />
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="bg-white rounded-lg border border-gray-300 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-bold text-prose-brand mb-4">Description *</h2>

          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Add detailed description of the property..."
            rows="6"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand resize-none text-sm"
          />
        </div>

        {/* Property Details */}
        <div className="bg-white rounded-lg border border-gray-300 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-bold text-prose-brand mb-4">Property Details</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-prose-black mb-1">Size (sq ft) *</label>
              <input
                type="number"
                name="size"
                value={formData.size}
                onChange={handleInputChange}
                min="0"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand text-sm"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-prose-black mb-1">Bedrooms *</label>
              <input
                type="number"
                name="bedrooms"
                value={formData.bedrooms}
                onChange={handleInputChange}
                min="1"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand text-sm"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-prose-black mb-1">Bathrooms *</label>
              <input
                type="number"
                name="bathrooms"
                value={formData.bathrooms}
                onChange={handleInputChange}
                min="1"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand text-sm"
              />
            </div>
          </div>
        </div>

        {/* Existing Images */}
        {property?.images && property.images.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-300 p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-bold text-prose-brand mb-4">Existing Images</h2>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
              {property.images.map((image) => (
                <div key={image.id} className="relative">
                  <img
                    src={image.url}
                    alt="Property"
                    className="w-full h-16 sm:h-20 md:h-24 object-cover rounded-lg border border-gray-300"
                  />
                  <button
                    type="button"
                    onClick={() => handleDeleteImage(image.id)}
                    disabled={isLoadingDeletions}
                    className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition duration-200 disabled:opacity-50"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add New Images */}
        <div className="bg-white rounded-lg border border-gray-300 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-bold text-prose-brand mb-4">Add New Images</h2>
          <p className="text-xs sm:text-sm text-prose-black/60 mb-4">Only JPG and PNG images are allowed, max 5MB each</p>

          {/* Drag and Drop Area */}
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`relative border-2 border-dashed rounded-lg p-4 sm:p-8 text-center transition duration-200 ${
              dragActive
                ? 'border-prose-brand bg-prose-brand/5'
                : 'border-gray-300 hover:border-prose-brand'
            }`}
          >
            <input
              type="file"
              multiple
              accept="image/jpeg,image/png"
              onChange={handleImageInput}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            <div className="flex flex-col items-center space-y-2">
              <Upload size={32} className="text-prose-brand/60" />
              <div>
                <p className="text-prose-brand font-medium text-sm sm:text-base">Drag and drop images here</p>
                <p className="text-prose-black/60 text-xs sm:text-sm">or click to browse</p>
              </div>
            </div>
          </div>

          {/* New Image Gallery */}
          {formData.newImages.length > 0 && (
            <div className="mt-4 sm:mt-6">
              <h3 className="text-xs sm:text-sm font-medium text-prose-black mb-2">
                New Images ({formData.newImages.length})
              </h3>
              <p className="text-xs text-prose-black/50 mb-3">Drag images to reorder or use arrow buttons.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
                {formData.newImages.map((image, index) => (
                  <div
                    key={index}
                    draggable
                    onDragStart={() => handleImageDragStart(index)}
                    onDragOver={handleImageDragOver}
                    onDrop={() => handleImageDrop(index)}
                    className={`relative group cursor-move transition ${
                      draggedIndex === index ? 'opacity-50' : ''
                    }`}
                  >
                    {/* Image Container */}
                    <div className="w-full aspect-square rounded-lg border-2 border-gray-300 overflow-hidden hover:border-prose-brand transition">
                      <img
                        src={URL.createObjectURL(image)}
                        alt={`New ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Image Index Badge */}
                    <div className="absolute top-1 left-1 sm:top-2 sm:left-2 px-2 py-1 bg-prose-brand text-white text-xs font-semibold rounded">
                      {index + 1}
                    </div>

                    {/* Drag Handle */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition rounded-lg">
                      <GripVertical size={24} className="text-white" />
                    </div>

                    {/* Action Buttons */}
                    <div className="absolute bottom-1 right-1 sm:bottom-2 sm:right-2 flex gap-1 sm:gap-2 opacity-0 group-hover:opacity-100 transition">
                      {/* Move Up Button */}
                      <button
                        type="button"
                        onClick={() => index > 0 && moveImage(index, index - 1)}
                        disabled={index === 0}
                        className="p-1 sm:p-1.5 bg-blue-500 text-white rounded-full hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        title="Move image up in order"
                      >
                        <ArrowUp size={14} className="sm:w-4 sm:h-4" />
                      </button>

                      {/* Move Down Button */}
                      <button
                        type="button"
                        onClick={() => index < formData.newImages.length - 1 && moveImage(index, index + 1)}
                        disabled={index === formData.newImages.length - 1}
                        className="p-1 sm:p-1.5 bg-blue-500 text-white rounded-full hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        title="Move image down in order"
                      >
                        <ArrowDown size={14} className="sm:w-4 sm:h-4" />
                      </button>

                      {/* Remove Button */}
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="p-1 sm:p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition"
                        title="Remove image"
                      >
                        <X size={14} className="sm:w-4 sm:h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Features */}
        <div className="bg-white rounded-lg border border-gray-300 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-bold text-prose-brand mb-4">Features *</h2>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 mb-4">
            {commonFeatures.map((feature) => (
              <label key={feature} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.features.some((f) => f.name === feature)}
                  onChange={() => toggleFeature(feature)}
                  className="w-4 h-4 rounded border-gray-300 text-prose-brand"
                />
                <span className="text-xs sm:text-sm text-prose-black">{feature}</span>
              </label>
            ))}
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-prose-black mb-2">
              Custom Features (Press Enter to add)
            </label>
            <input
              type="text"
              onKeyPress={(e) => handleArrayInput(e, 'features')}
              placeholder="Add custom feature..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand text-sm"
            />
            <div className="flex flex-wrap gap-2 mt-3">
              {formData.features.map((feature, index) => (
                <span
                  key={feature.id || `custom-${index}`}
                  className="px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm border border-prose-gold/60 text-prose-brand font-medium flex items-center space-x-2"
                >
                  <span>{feature.name}</span>
                  <button
                    type="button"
                    onClick={() => {
                      if (feature.id) {
                        handleDeleteFeature(feature.id);
                      } else {
                        removeArrayItem('features', index);
                      }
                    }}
                    className="hover:text-red-600"
                    disabled={isLoadingDeletions}
                  >
                    <X size={12} className="sm:w-3.5 sm:h-3.5" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex gap-2 sm:gap-3">
          <button
            type="submit"
            disabled={isUpdating || isLoadingDeletions}
            className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2 bg-prose-brand text-white rounded-lg hover:bg-prose-brand/90 transition duration-200 font-semibold text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus size={18} />
            <span>{isUpdating ? 'Saving...' : 'Save Changes'}</span>
          </button>
          <button
            type="button"
            onClick={() => navigate('/properties')}
            className="px-4 sm:px-6 py-2 border border-gray-300 text-prose-brand rounded-lg hover:bg-gray-100 transition duration-200 font-semibold text-xs sm:text-sm"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditProperty;
